/** SUNTRONIX ONE — Tenant Home (marketplace-style dashboard).
 *  Featured/standard products, recently viewed, saved products, saved carts,
 *  Buy Again, and at-a-glance stats (open requests, quotes, deliveries). */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Store, ScrollText, ShoppingCart, Heart, Clock, RotateCcw, PackageSearch, Truck,
  Loader2, ChevronRight, FileText, Bookmark,
} from "lucide-react";

const money = (n, cur = "INR") => n == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(n).toLocaleString("en-IN")}`;
const PriceTag = ({ pricing }) => pricing?.mode === "PRICE"
  ? <span className="text-[13px] font-semibold text-slate-900">{money(pricing.unit_price, pricing.currency)}</span>
  : <Badge className="bg-amber-50 text-amber-700 text-[10px]">Request Quote</Badge>;

function MiniCard({ p, onOpen, onAdd }) {
  return (
    <div className="min-w-[168px] w-[168px] bg-white border border-slate-200 rounded-lg p-3 flex flex-col" data-testid={`home-mini-${p.product_code}`}>
      <button onClick={() => onOpen(p.product_code)} className="text-left">
        <div className="h-20 flex items-center justify-center mb-1.5">
          {p.primary_image ? <img src={p.primary_image} alt="" className="max-h-20 object-contain" /> : <PackageSearch className="h-8 w-8 text-slate-200" />}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.brand}</div>
        <div className="text-[12px] font-medium text-slate-800 line-clamp-2 min-h-[2rem]">{p.product_name}</div>
      </button>
      <div className="mt-1.5 flex items-center justify-between">
        <PriceTag pricing={p.pricing} />
        <button onClick={() => onAdd(p.product_code)} title="Add to cart" className="h-7 w-7 rounded-md bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center"><ShoppingCart className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

function Row({ title, icon: Icon, items, onOpen, onAdd, testid, empty }) {
  if (!items || items.length === 0) return empty ? (
    <div data-testid={testid}><div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><Icon className="h-4 w-4 text-sky-600" />{title}</div><div className="text-[12px] text-slate-400">{empty}</div></div>
  ) : null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><Icon className="h-4 w-4 text-sky-600" />{title}</div>
      <div className="flex gap-2 overflow-x-auto pb-1">{items.map((p) => <MiniCard key={p.product_code} p={p} onOpen={onOpen} onAdd={onAdd} />)}</div>
    </div>
  );
}

export default function TenantHome() {
  const nav = useNavigate();
  const [home, setHome] = useState(null);
  const [recent, setRecent] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [savedCarts, setSavedCarts] = useState([]);
  const [history, setHistory] = useState([]);
  const [blocked, setBlocked] = useState(null);

  const load = useCallback(async () => {
    try {
      const [h, r, w, sc, oh] = await Promise.all([
        api.get("/tenant-catalogue/home"),
        api.get("/tenant-catalogue/recent"),
        api.get("/tenant-catalogue/wishlist"),
        api.get("/tenant-catalogue/saved-carts"),
        api.get("/tenant-catalogue/order-history"),
      ]);
      setHome(h.data); setRecent(r.data.items); setWishlist(w.data.items);
      setSavedCarts(sc.data.items); setHistory(oh.data.items);
    } catch (e) { setBlocked(e.response?.data?.detail || "Catalogue unavailable"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addToCart = async (code) => {
    try { await api.post("/tenant-catalogue/cart/items", { product_code: code, quantity: 1 }); toast.success("Added to cart"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const openProduct = () => nav("/tenant-catalogue");
  const restoreCart = async (id) => { await api.post(`/tenant-catalogue/saved-carts/${id}/restore`); toast.success("Cart restored"); nav("/tenant-catalogue"); };
  const buyAgain = async (id) => { const { data } = await api.post("/tenant-catalogue/reorder", { enquiry_id: id }); toast.success(`Rebuilt cart (${data.cart.count} items)${data.skipped.length ? `, ${data.skipped.length} unavailable` : ""}`); nav("/tenant-catalogue"); };

  if (blocked) return (
    <div className="max-w-2xl mx-auto p-10 text-center" data-testid="home-blocked">
      <Store className="h-10 w-10 mx-auto mb-3 text-slate-300" />
      <p className="text-sm text-slate-500">{blocked}</p>
    </div>
  );
  if (!home) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  const s = home.stats;
  const tiles = [
    { label: "Open requests", value: s.open_requests, icon: Clock, to: "/my-quotes" },
    { label: "Quotations", value: s.quotes, icon: FileText, to: "/my-quotes" },
    { label: "Cart", value: home.cart.count, icon: ShoppingCart, to: "/tenant-catalogue" },
    { label: "Saved products", value: s.wishlist, icon: Heart, to: null },
    { label: "Saved carts", value: s.saved_carts, icon: Bookmark, to: null },
    { label: "Deliveries", value: s.deliveries, icon: Truck, to: null },
  ];

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6" data-testid="tenant-home">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Welcome</div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Store className="h-6 w-6 text-sky-600" />SUNTRONIX</h1>
          <p className="text-sm text-slate-500">Your procurement home.</p>
        </div>
        <Button onClick={() => nav("/tenant-catalogue")} data-testid="home-browse" className="bg-sky-600 hover:bg-sky-700 text-white">Browse catalogue<ChevronRight className="h-4 w-4 ml-1" /></Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {tiles.map((t) => (
          <button key={t.label} data-testid={`home-tile-${t.label.toLowerCase().replace(/ /g, '-')}`} onClick={() => t.to && nav(t.to)}
            className={"bg-white border border-slate-200 rounded-lg p-3 text-left " + (t.to ? "hover:border-slate-300 hover:shadow-sm" : "")}>
            <t.icon className="h-4 w-4 text-slate-400 mb-1" />
            <div className="text-xl font-semibold text-slate-900">{t.value}</div>
            <div className="text-[11px] text-slate-500">{t.label}</div>
          </button>
        ))}
      </div>

      {history.length > 0 && (
        <div data-testid="home-buy-again">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><RotateCcw className="h-4 w-4 text-sky-600" />Buy again</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {history.map((o) => (
              <div key={o.id} className="min-w-[220px] bg-white border border-slate-200 rounded-lg p-3" data-testid={`home-order-${o.id}`}>
                <div className="text-[12px] font-medium text-slate-800">{o.ref}</div>
                <div className="text-[11px] text-slate-400">{o.item_count} item(s) · {o.date ? new Date(o.date).toLocaleDateString() : ""}</div>
                <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{o.products.map((p) => p.product_name).filter(Boolean).join(", ")}</div>
                <Button size="sm" variant="outline" onClick={() => buyAgain(o.id)} data-testid={`home-reorder-${o.id}`} className="mt-2 w-full h-8"><RotateCcw className="h-3.5 w-3.5 mr-1" />Add all to cart</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {savedCarts.length > 0 && (
        <div data-testid="home-saved-carts">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-2"><Bookmark className="h-4 w-4 text-sky-600" />Saved carts</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {savedCarts.map((sc) => (
              <div key={sc.id} className="min-w-[200px] bg-white border border-slate-200 rounded-lg p-3" data-testid={`home-savedcart-${sc.id}`}>
                <div className="text-[12px] font-medium text-slate-800">{sc.name}</div>
                <div className="text-[11px] text-slate-400">{sc.item_count} item(s)</div>
                <Button size="sm" variant="outline" onClick={() => restoreCart(sc.id)} className="mt-2 w-full h-8">Restore cart</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Row title={home.featured_label} icon={Store} items={home.featured} onOpen={openProduct} onAdd={addToCart} testid="home-featured" />
      <Row title="Recently viewed" icon={Clock} items={recent} onOpen={openProduct} onAdd={addToCart} testid="home-recent" />
      <Row title="Saved products" icon={Heart} items={wishlist} onOpen={openProduct} onAdd={addToCart} testid="home-wishlist" empty="Tap the heart on any product to save it here." />
    </div>
  );
}
